import countries from '@data/countries';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import useSWR from 'swr';
import BrandGrid from '@/components/brand-grid';
import BrowseMore from '@/components/browse-more';
import CategoriesGrid from '@/components/categories-grid';
import EarlyExit from '@/components/early-exit';
import FaqList from '@/components/faq-list';
import HowItWorks from '@/components/how-it-works';
import { FAQSchema } from '@/components/json-ld';
import PageHero from '@/components/page-hero';
import SEO from '@/components/seo';
import StablecoinsTeaser from '@/components/stablecoins-teaser';
import { Button, CtaPanel, Section, SectionHeader, StatsRow } from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import { countryNameInSentence } from '@/utils/country-name';
import { shareImage } from '@/utils/share-image';

/* How many categories the "popular categories" grid shows. */
const MAX_CATEGORIES = 8;

const FlagLabel = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

const Flag = styled.img`
  border-radius: 9999px;
  box-shadow: 0 0 0 1px var(--border);
  height: 24px;
  object-fit: cover;
  width: 24px;
`;

const Stats = styled(StatsRow)`
  margin: 24px auto 0;
  max-width: 960px;
`;

const Brands = styled.div`
  margin-top: 40px;
`;

const Categories = styled(CategoriesGrid)`
  margin-top: 40px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const Faqs = styled(FaqList)`
  margin-top: 40px;
`;

const fetcher = (...args) =>
  fetch(...args)
    .then((res) => res.json())
    .then((result) =>
      result.groups.length > 0 ? result.groups[0].categories[0].brands.slice(0, 12) : []
    );

// --- Page Component ---

const BrandsForCountry = ({
  brandCount,
  brands,
  canonicalUrl,
  country,
  popularCategories: categories,
  router,
}) => {
  const t = useTranslations('country');
  const cT = useTranslations('categories');
  const initialFallbackData = brands;
  const matchingCountry = countries.find((c) => c.slug === router?.query?.country);
  const countryId = matchingCountry ? matchingCountry.id : country.id;
  const apiUrl = `${config.commerceApiUrl}/search?countryId=${countryId}&minimal=true`;

  const { data } = useSWR(apiUrl, fetcher, {
    fallbackData: initialFallbackData,
    revalidateOnMount: true,
  });

  const inCountry = countryNameInSentence(country, router?.locale);
  const browseUrl = `/buy/${country.slug}`;

  const faqs = [1, 2, 3, 4].map((n) => ({
    question: t(`faq${n}Question`, { country: inCountry }),
    answer: t(`faq${n}Answer`, { country: inCountry }),
  }));

  // Only numbers we can stand behind: both come from the catalogue at build time.
  const stats = [
    brandCount > 0 && {
      value: brandCount.toLocaleString(router?.locale),
      label: t('statsBrands'),
    },
    categories.length > 0 && { value: String(categories.length), label: t('statsCategories') },
    { value: t('statsDeliveryValue'), label: t('statsDelivery') },
  ].filter(Boolean);

  const categoryLinks = categories.slice(0, MAX_CATEGORIES).map(({ slug }) => ({
    slug,
    href: `/buy/${country.slug}/${slug}`,
    label: cT.has(slug) ? cT(slug) : slug,
  }));

  return (
    <>
      <SEO
        title={t('seoTitle', { country: inCountry })}
        description={t('seoDescription', { country: inCountry })}
        url={canonicalUrl}
        imageUrl={shareImage.country(country.slug, router?.locale)}
        imageAlt={t('title', { country: inCountry })}
        // A country with nothing to buy has no value in the index (it is left
        // out of the sitemap too: data/sitemap-links.js).
        noindex={brands.length === 0}
      />
      <FAQSchema faqs={faqs} />

      <PageHero
        eyebrow={
          <FlagLabel>
            <Flag
              alt={t('flagAlt', { country: country.name })}
              height={24}
              src={`https://assets.bidali.com/flags/${country.id}.avif`}
              width={24}
            />
            {country.name}
          </FlagLabel>
        }
        title={t('title', { country: inCountry })}
        description={t('intro', { country: inCountry })}
        actions={[{ label: t('browseAll'), href: browseUrl }]}
      />

      {stats.length > 1 && (
        <Section id="stats" $spacing="none">
          <Stats stats={stats} />
        </Section>
      )}

      <Section id="brands">
        <SectionHeader
          title={t('brandsTitle', { country: inCountry })}
          description={t('brandsDescription', { country: inCountry })}
        />
        <Brands>
          <BrandGrid brands={data || brands} />
        </Brands>
        <Actions>
          <Button $size="pill" $variant="outline" href={browseUrl}>
            {t('browseAllBrands', { country: inCountry })}
          </Button>
        </Actions>
      </Section>

      {categoryLinks.length > 0 && (
        <Section id="categories" $background="color-mix(in oklab, var(--muted) 50%, transparent)">
          <SectionHeader title={t('categoriesTitle', { country: inCountry })} />
          <Categories categories={categoryLinks} columns={4} />
        </Section>
      )}

      <HowItWorks />

      <StablecoinsTeaser />

      <Section id="faq">
        <SectionHeader align="center" title={t('faqTitle')} />
        <Faqs faqs={faqs} />
      </Section>

      {/* Not the categories already shown above. */}
      <BrowseMore exclude={categoryLinks.map(({ slug }) => slug)} />

      <Section id="cta">
        <CtaPanel
          title={t('ctaTitle', { country: inCountry })}
          description={t('ctaSubtitle', { country: inCountry })}
          actions={[{ label: t('browseAll'), href: browseUrl }]}
        />
      </Section>
    </>
  );
};

// Not `categories`: _app.js passes every page the full category list under that name.
const Index = ({ brandCount = 0, brands = [], canonicalUrl, country, popularCategories = [] }) => {
  const router = useRouter();

  return (
    <EarlyExit
      Component={BrandsForCountry}
      condition={!router.query.country}
      brandCount={brandCount}
      brands={brands}
      country={country}
      canonicalUrl={canonicalUrl}
      popularCategories={popularCategories}
    />
  );
};

export async function getStaticPaths({ locales }) {
  const paths = [];

  for (let i = 0; i < locales.length; i++) {
    const locale = locales[i];

    countries.forEach((country) => {
      paths.push({
        params: { country: country.slug },
        locale,
      });
    });
  }

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params, locale }) {
  let country = countries.find((c) => c.slug === params.country);
  let brands = [];
  let brandCount = 0;
  let popularCategories = [];

  if (!country) {
    try {
      // Server-only: identify as a trusted caller (GIFTCARDS_API_SERVICE_KEY).
      const { fetchApi } = require('@/api');
      country = await fetchApi(`${config.commerceApiUrl}/countries?slug=${params.country}`)
        .then((res) => res.json())
        .then((result) => result[0]);
      const apiUrl = `${config.commerceApiUrl}/search?countryId=${country.id}&minimal=true`;
      brands = await fetchApi(apiUrl)
        .then((res) => res.json())
        .then((result) =>
          result.groups.length > 0 ? result.groups[0].categories[0].brands.slice(0, 12) : []
        );
    } catch (error) {
      console.error('Error fetching country data:', error);
      return { notFound: true };
    }
  } else {
    // Build data, required here so none of it reaches the client bundle.
    const { enabledBrands } = require('@data/index');
    const { mergeCategories } = require('@data/merge-categories');
    const { summarizeCountryCatalogue } = require('@/utils/country-catalogue');
    const summary = summarizeCountryCatalogue({
      brands: enabledBrands,
      // cats.json includes the hidden categories; the merge only covers an older file.
      categories: mergeCategories(
        require('@data/cats.json'),
        require('@data/extra-categories.json')
      ),
      countryId: country.id,
      rawBrands: require('@data/brands.json'),
    });

    brands = summary.featured;
    brandCount = summary.brandCount;
    popularCategories = summary.categories;
  }

  brands = (brands || [])
    .map((b) => {
      const { id, slug, backgroundColor, name, imageUrl, type, country } = b;
      return {
        id,
        slug,
        countrySlug: country.slug,
        backgroundColor: backgroundColor ?? null,
        name,
        imageUrl,
        type: type ?? null,
      };
    })
    .slice(0, 12);

  const canonicalUrl = `${config.rootUrl}/${locale !== i18n.defaultLocale ? `${locale}/` : ''}brands/${params.country}`;

  return {
    props: {
      brands,
      brandCount,
      popularCategories,
      // Only what the page renders.
      country: { id: country.id, name: country.name, slug: country.slug },
      canonicalUrl,
      messages: loadMessages(locale),
    },
  };
}

export default Index;
